import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { LearnerProfileDialogComponent } from '../../../components/dialogs/learner-profile-dialog/learner-profile-dialog.component';

type LearnerSortColumn =
  | 'name'
  | 'email'
  | 'user_id'
  | 'source'
  | 'client'
  | 'created_at'
  | 'last_active_at';

type SortDirection = 'asc' | 'desc' | null;

export interface AdminLearner {
  id: string;
  user_id: string | null;
  source: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  created_at: string | null;
  last_active_at: string | null;
  client_id: string;
  client_name: string;
  client_email: string;
  duplicate_count?: number;
}

@Component({
  selector: 'app-admin-learners',
  templateUrl: './learners.component.html',
  styleUrl: './learners.component.scss',
})
export class AdminLearnersComponent implements OnInit {
  learners: AdminLearner[] = [];
  isLoading = true;
  searchText = '';
  currentPage = 0;
  pageSize = 25;
  pageSizeOptions = [25, 50, 100, 250];
  filters: Record<LearnerSortColumn, string> = {
    name: '',
    email: '',
    user_id: '',
    source: '',
    client: '',
    created_at: '',
    last_active_at: '',
  };
  sortColumn: LearnerSortColumn = 'created_at';
  sortDirection: SortDirection = 'desc';

  constructor(
    private http: HttpClient,
    private dialogService: NbDialogService,
    protected t: TranslateService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.isLoading = true;
    this.http
      .get<{ data: AdminLearner[] }>(`${environment.tutorsBackend}/admin/learners`)
      .subscribe({
        next: (res) => {
          this.learners = res.data || [];
          this.isLoading = false;
        },
        error: () => (this.isLoading = false),
      });
  }

  get filteredLearners(): AdminLearner[] {
    const global = this.normalize(this.searchText);
    const filters = Object.fromEntries(
      Object.entries(this.filters).map(([key, value]) => [key, this.normalize(value)])
    ) as Record<LearnerSortColumn, string>;

    const filtered = this.learners.filter((learner) => {
      if (global && !this.rowSearchText(learner).includes(global)) {
        return false;
      }

      return (Object.keys(filters) as LearnerSortColumn[]).every((column) => {
        const value = filters[column];
        if (!value) return true;
        return this.filterValue(learner, column).includes(value);
      });
    });

    return this.sortedLearners(filtered);
  }

  get pagedLearners(): AdminLearner[] {
    const rows = this.filteredLearners;
    const maxPage = Math.max(Math.ceil(rows.length / this.pageSize) - 1, 0);
    const page = Math.min(this.currentPage, maxPage);
    const start = page * this.pageSize;
    return rows.slice(start, start + this.pageSize);
  }

  getName(l: AdminLearner): string {
    if (l.first_name || l.last_name) {
      return `${l.first_name ?? ''} ${l.last_name ?? ''}`.trim();
    }
    return l.email ?? '—';
  }

  get sourceOptions(): string[] {
    return Array.from(
      new Set(this.learners.map((l) => l.source).filter((source): source is string => !!source))
    ).sort((a, b) => a.localeCompare(b));
  }

  hasActiveFilters(): boolean {
    return !!this.searchText || Object.values(this.filters).some((value) => !!value);
  }

  clearFilters(): void {
    this.searchText = '';
    (Object.keys(this.filters) as LearnerSortColumn[]).forEach((key) => {
      this.filters[key] = '';
    });
    this.resetPage();
  }

  resetPage(): void {
    this.currentPage = 0;
  }

  onPageChange(page: number): void {
    const totalPages = Math.ceil(this.filteredLearners.length / this.pageSize);
    this.currentPage = Math.max(0, Math.min(page, Math.max(totalPages - 1, 0)));
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.resetPage();
  }

  toggleSort(column: LearnerSortColumn): void {
    if (this.sortColumn !== column) {
      this.sortColumn = column;
      this.sortDirection = 'asc';
      this.resetPage();
      return;
    }

    if (this.sortDirection === 'asc') {
      this.sortDirection = 'desc';
    } else if (this.sortDirection === 'desc') {
      this.sortDirection = null;
    } else {
      this.sortDirection = 'asc';
    }
    this.resetPage();
  }

  sortIcon(column: LearnerSortColumn): string {
    if (this.sortColumn !== column || !this.sortDirection) {
      return 'swap-outline';
    }
    return this.sortDirection === 'asc' ? 'arrow-up-outline' : 'arrow-down-outline';
  }

  private sortedLearners(rows: AdminLearner[]): AdminLearner[] {
    if (!this.sortDirection) {
      return rows;
    }

    const direction = this.sortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const valueA = this.sortValue(a, this.sortColumn);
      const valueB = this.sortValue(b, this.sortColumn);

      if (typeof valueA === 'number' || typeof valueB === 'number') {
        return ((Number(valueA) || 0) - (Number(valueB) || 0)) * direction;
      }

      return String(valueA).localeCompare(String(valueB), undefined, {
        sensitivity: 'base',
        numeric: true,
      }) * direction;
    });
  }

  private sortValue(learner: AdminLearner, column: LearnerSortColumn): string | number {
    if (column === 'created_at' || column === 'last_active_at') {
      return learner[column] ? new Date(learner[column] as string).getTime() : 0;
    }
    return this.filterValue(learner, column);
  }

  private filterValue(learner: AdminLearner, column: LearnerSortColumn): string {
    switch (column) {
      case 'name':
        return this.normalize(this.getName(learner));
      case 'email':
        return this.normalize(learner.email);
      case 'user_id':
        return this.normalize(learner.user_id);
      case 'source':
        return this.normalize(learner.source);
      case 'client':
        return this.normalize(`${learner.client_name || ''} ${learner.client_email || ''} ${learner.client_id || ''}`);
      case 'created_at':
        return this.normalizeDate(learner.created_at);
      case 'last_active_at':
        return this.normalizeDate(learner.last_active_at);
    }
  }

  private rowSearchText(learner: AdminLearner): string {
    return this.normalize(
      [
        this.getName(learner),
        learner.email,
        learner.user_id,
        learner.source,
        learner.client_name,
        learner.client_email,
        learner.client_id,
        this.normalizeDate(learner.created_at),
        this.normalizeDate(learner.last_active_at),
      ].join(' ')
    );
  }

  private normalizeDate(value: string | null): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.normalize(value);
    }
    return this.normalize(
      [
        value,
        date.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }),
        date.toLocaleDateString('de-DE'),
      ].join(' ')
    );
  }

  private normalize(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  trackLearner = (_: number, learner: AdminLearner) => learner.id;

  openProfile(l: AdminLearner) {
    this.dialogService.open(LearnerProfileDialogComponent, {
      context: {
        learnerId: l.id,
        learnerName: this.getName(l),
        learnerEmail: l.email || '',
      },
    });
  }
}
